import { Component, ElementRef, EventEmitter, Input, NgZone, OnDestroy, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';

/**
 *  KAMERÁS VONALKÓD-OLVASÓ – a telefon (vagy a gép) kamerája mint vonalkód-olvasó.
 *
 *  A mobil Kassza a pénztárgépet helyettesíti, ott nincs kézi olvasó: a kamera olvas (user, 2026-10-12).
 *  A gomb ott jelenik meg, ahol a kézi olvasó is ír (Kassza keresője, Készletkezelés mozgás/leltár/cikk);
 *  a beolvasott kód ugyanazon az úton megy tovább, mint a kézi olvasó Enterje.
 *
 *  A zxing a böngészőben és a natív app WebView-jában is fut (a kamera-engedély a manifestben és az
 *  Info.plist-ben megvan), ezért nincs külön natív plugin. A könyvtár csak megnyitáskor töltődik be.
 *
 *  `folyamatos`: nyitva marad, és minden olvasást jelez (leltár: minden olvasás +1). A kamera egy
 *  vonalkódot másodpercenként sokszor lát, ezért ugyanaz a kód csak akkor számít újra, ha közben
 *  legalább SZUNET ms-ig nem volt a képben.
 */
const SZUNET = 900;

@Component({
  selector: 'app-kamera-olvaso',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './kamera-olvaso.component.html',
  styleUrls: ['./kamera-olvaso.component.scss'],
})
export class KameraOlvasoComponent implements OnDestroy {
  @Input() folyamatos = false;
  @Input() cim = 'Vonalkód olvasása kamerával';
  @Input() tiltva = false;
  @Output() olvasott = new EventEmitter<string>();
  @ViewChild('video') videoRef?: ElementRef<HTMLVideoElement>;

  nyitva = false;
  hiba = '';
  utolso = '';
  darab = 0;
  private vezerlo: { stop: () => void } | null = null;
  private utolsoIdo = 0;

  constructor(private zone: NgZone) {}

  /** Van-e egyáltalán kamera-hozzáférés (régi böngésző, nem biztonságos oldal: nincs). */
  get elerheto(): boolean { return !!navigator.mediaDevices?.getUserMedia; }

  async nyit() {
    if (this.tiltva || this.nyitva) return;
    this.hiba = ''; this.utolso = ''; this.darab = 0; this.nyitva = true;
    await new Promise((r) => setTimeout(r));          // a <video> megjelenjen
    try {
      const { BrowserMultiFormatReader, BarcodeFormat } = await import('@zxing/browser');
      const olvaso = new BrowserMultiFormatReader(undefined, { delayBetweenScanAttempts: 120 });
      olvaso.possibleFormats = [BarcodeFormat.EAN_13, BarcodeFormat.EAN_8, BarcodeFormat.UPC_A, BarcodeFormat.UPC_E,
        BarcodeFormat.CODE_128, BarcodeFormat.CODE_39, BarcodeFormat.ITF, BarcodeFormat.QR_CODE, BarcodeFormat.DATA_MATRIX];
      if (!this.nyitva || !this.videoRef) return;
      this.vezerlo = await olvaso.decodeFromConstraints(
        { video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 }, height: { ideal: 720 } }, audio: false },
        this.videoRef.nativeElement,
        (eredmeny) => { if (eredmeny) this.zone.run(() => this.talalat(eredmeny.getText())); },
      );
      if (!this.nyitva) this.leallit();               // közben bezárták
    } catch (e: any) {
      const nev = e?.name || '';
      this.hiba = nev === 'NotAllowedError' ? 'A kamera használatát nem engedélyezted. A telefon beállításaiban engedélyezd az appnak.'
        : nev === 'NotFoundError' || nev === 'OverconstrainedError' ? 'Nem található kamera.'
        : 'A kamera nem indítható el.';
      this.leallit();
    }
  }

  private talalat(kod: string) {
    const most = Date.now();
    //  Ugyanaz a kód csak akkor számít újra, ha közben SZUNET ideig nem látszott (a következő darabot
    //  mutatják) – a képben maradó vonalkód nem számol magától tovább. Minden látás frissíti az időt.
    const ujra = kod === this.utolso && most - this.utolsoIdo < SZUNET;
    this.utolsoIdo = most;
    if (!kod || ujra) return;
    this.utolso = kod; this.darab++;
    this.jelez();
    this.olvasott.emit(kod);
    if (!this.folyamatos) this.bezar();
  }

  /** Rezgés (telefonon) és rövid hang – a pénztáros a kijelzőre nézés nélkül is tudja, hogy olvasott. */
  private jelez() {
    try { navigator.vibrate?.(60); } catch { /* nem támogatott */ }
    try {
      const Ctx = (window as any).AudioContext || (window as any).webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx(); const osc = ctx.createOscillator(); const g = ctx.createGain();
      osc.frequency.value = 2400; g.gain.value = 0.08; osc.connect(g); g.connect(ctx.destination);
      osc.start(); osc.stop(ctx.currentTime + 0.07); osc.onended = () => ctx.close();
    } catch { /* hang nélkül is megy */ }
  }

  private leallit() {
    try { this.vezerlo?.stop(); } catch { /* már leállt */ }
    this.vezerlo = null;
  }
  bezar() { this.nyitva = false; this.leallit(); }
  ngOnDestroy() { this.leallit(); }
}
